import { Check, ChevronDown, Mail, MapPin, Phone, Quote, Sparkles } from 'lucide-react';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { fetchPricing } from '../lib/api';
import { submitForm } from '../lib/forms';
import { Reveal } from '../motion/Reveal';
import { StaggerGroup, StaggerItem } from '../motion/Stagger';
import { StatCounter } from './StatCounter';

import type {
  ContactBlock,
  CtaBlock,
  FaqBlock,
  FeatureGridBlock,
  FeatureSplitBlock,
  LogosBlock,
  PageBlock,
  PlansBlock,
  PricingBlock,
  PricingPlan,
  RichTextBlock,
  StatsBlock,
  TestimonialsBlock,
} from '@saas-bot/types';

import { deriveHeroContent } from './hero/hero.constants';
import { HeroCarousel } from './hero/HeroCarousel';
import { BlockIcon, Container, Eyebrow, SectionHeading, SmartLink } from './ui';

function Section({
  children,
  tone = 'cream',
  className = '',
  id,
}: {
  children: ReactNode;
  tone?: 'cream' | 'white' | 'ink';
  className?: string;
  id?: string;
}): JSX.Element {
  // On the dark stage the three tones are three depths, not three colours:
  // the page itself, a faintly raised band, and a violet-lit panel.
  const tones = {
    cream: 'bg-transparent',
    white: 'bg-[var(--color-surface)]',
    ink: 'bg-[linear-gradient(160deg,rgba(144,97,255,0.14),rgba(11,16,36,0.9)_55%,rgba(5,6,13,1))] text-white',
  } as const;
  return (
    <section id={id} className={`relative py-20 sm:py-28 ${tones[tone]} ${className}`}>
      {tone !== 'cream' ? (
        <div className="pointer-events-none absolute inset-0 mk-grid opacity-60" aria-hidden />
      ) : null}
      <Container className="relative">{children}</Container>
    </section>
  );
}

/* --------------------------------- Hero --------------------------------- */
/* The premium "Conversation-to-Conversion Engine" hero lives in ./hero. This
 * block renderer just maps the CMS hero block onto its typed content. */

/* --------------------------------- Logos -------------------------------- */

function Logos(block: LogosBlock): JSX.Element {
  return (
    <Section tone="white" className="!py-14">
      <div className="flex flex-col items-center gap-8">
        {block.heading ? (
          <p className="text-sm font-medium uppercase tracking-[0.14em] text-[var(--color-ink-muted)]">
            {block.heading}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {block.logos.map((logo) =>
            logo.imageUrl ? (
              <img key={logo.name} src={logo.imageUrl} alt={logo.name} className="h-7 opacity-70" />
            ) : (
              <span
                key={logo.name}
                className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-ink-muted)]"
              >
                {logo.name}
              </span>
            ),
          )}
        </div>
      </div>
    </Section>
  );
}

/* --------------------------------- Stats -------------------------------- */

function Stats(block: StatsBlock): JSX.Element {
  return (
    <Section tone="white">
      {block.heading ? (
        <p className="mb-12 text-center text-sm font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-muted)]">
          {block.heading}
        </p>
      ) : null}
      <StaggerGroup className="grid grid-cols-2 gap-8 sm:gap-12 lg:grid-cols-4">
        {block.items.map((item) => (
          <StaggerItem key={item.label} className="text-center">
            <p className="accent-text font-[var(--font-display)] text-4xl font-bold sm:text-5xl">
              <StatCounter value={item.value} />
            </p>
            <p className="mt-2 text-sm text-[var(--color-ink-soft)]">{item.label}</p>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </Section>
  );
}

/* ------------------------------ Feature grid ---------------------------- */

function FeatureGrid(block: FeatureGridBlock): JSX.Element {
  const cols =
    block.columns === 2
      ? 'sm:grid-cols-2'
      : block.columns === 4
        ? 'sm:grid-cols-2 lg:grid-cols-4'
        : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <Section>
      <SectionHeading
        eyebrow={block.eyebrow}
        heading={block.heading}
        subheading={block.subheading}
      />
      <StaggerGroup className={`mt-14 grid gap-6 ${cols}`}>
        {block.features.map((feature) => (
          <StaggerItem
            key={feature.title}
            className="group h-full rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-surface)] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[var(--accent)] hover:shadow-[0_24px_50px_-30px_rgba(144,97,255,0.55)]"
          >
            <span className="accent-text grid h-12 w-12 place-items-center rounded-2xl bg-[var(--color-violet-100)]">
              <BlockIcon name={feature.icon} className="h-6 w-6" />
            </span>
            <h3 className="mt-5 text-lg font-semibold text-[var(--color-ink)]">{feature.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-ink-soft)]">
              {feature.body}
            </p>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </Section>
  );
}

/* ------------------------------ Feature split --------------------------- */

function SplitVisual({ imageUrl, bullets }: { imageUrl: string; bullets: string[] }): JSX.Element {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className="w-full rounded-[var(--radius-2xl)] border border-[var(--color-line)] object-cover"
      />
    );
  }
  return (
    <div className="rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-gradient-to-br from-[var(--color-cream-100)] to-[var(--color-violet-100)] p-8">
      <ul className="space-y-4">
        {bullets.map((bullet) => (
          <li
            key={bullet}
            className="flex items-center gap-3 rounded-2xl bg-[var(--color-surface-strong)] px-4 py-3.5 text-sm font-medium text-[var(--color-ink)] shadow-sm backdrop-blur"
          >
            <span className="accent-bg grid h-6 w-6 shrink-0 place-items-center rounded-full text-white">
              <Check className="h-3.5 w-3.5" aria-hidden />
            </span>
            {bullet}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeatureSplit(block: FeatureSplitBlock): JSX.Element {
  return (
    <Section tone="white">
      <div className="flex flex-col gap-20 sm:gap-28">
        {block.items.map((item) => (
          <Reveal
            key={item.title}
            className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
              item.reverse ? 'lg:[&>*:first-child]:order-2' : ''
            }`}
          >
            <div className="flex flex-col items-start gap-4">
              {item.eyebrow ? <Eyebrow>{item.eyebrow}</Eyebrow> : null}
              <h3 className="font-[var(--font-display)] text-3xl font-semibold leading-tight text-[var(--color-ink)]">
                {item.title}
              </h3>
              <p className="text-lg leading-relaxed text-[var(--color-ink-soft)]">{item.body}</p>
              {item.bullets.length > 0 && item.imageUrl ? (
                <ul className="mt-2 space-y-2.5">
                  {item.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-3 text-[var(--color-ink)]">
                      <Check className="accent-text h-5 w-5 shrink-0" aria-hidden />
                      {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <SplitVisual imageUrl={item.imageUrl} bullets={item.bullets} />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------- Pricing ------------------------------- */

function Pricing(block: PricingBlock): JSX.Element {
  return (
    <Section>
      <SectionHeading
        eyebrow={block.eyebrow}
        heading={block.heading}
        subheading={block.subheading}
      />
      <StaggerGroup className="mt-14 grid items-stretch gap-6 lg:grid-cols-3">
        {block.tiers.map((tier) => (
          <StaggerItem
            key={tier.name}
            className={`flex h-full flex-col rounded-[var(--radius-2xl)] border p-8 ${
              tier.highlighted
                ? 'accent-bg border-transparent text-white shadow-[0_40px_80px_-40px_rgba(144,97,255,0.7)] lg:-my-4 lg:py-12'
                : 'border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)]'
            }`}
          >
            {tier.highlighted ? (
              <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Most popular
              </span>
            ) : null}
            <h3 className="text-lg font-semibold">{tier.name}</h3>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-[var(--font-display)] text-4xl font-bold">{tier.price}</span>
              {tier.period ? (
                <span
                  className={tier.highlighted ? 'text-white/70' : 'text-[var(--color-ink-muted)]'}
                >
                  {tier.period}
                </span>
              ) : null}
            </div>
            <p
              className={`mt-3 text-sm ${tier.highlighted ? 'text-white/80' : 'text-[var(--color-ink-soft)]'}`}
            >
              {tier.description}
            </p>
            <ul className="mt-6 flex-1 space-y-3">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm">
                  <Check
                    className={`mt-0.5 h-4 w-4 shrink-0 ${tier.highlighted ? 'text-white' : 'accent-text'}`}
                    aria-hidden
                  />
                  <span
                    className={tier.highlighted ? 'text-white/90' : 'text-[var(--color-ink-soft)]'}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <SmartLink
                href={tier.cta.href}
                className={`inline-flex w-full items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                  tier.highlighted
                    ? 'bg-white text-[var(--accent-ink)] hover:brightness-95'
                    : 'accent-bg text-white hover:brightness-110'
                }`}
              >
                {tier.cta.label}
              </SmartLink>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </Section>
  );
}

/* --------------------------- Plans (live pricing) ----------------------- */

function PlanCard({ plan }: { plan: PricingPlan }): JSX.Element {
  return (
    <div
      className={`flex h-full flex-col rounded-[var(--radius-2xl)] border p-8 ${
        plan.highlighted
          ? 'accent-bg border-transparent text-white shadow-[0_40px_80px_-40px_rgba(144,97,255,0.7)] lg:-my-4 lg:py-12'
          : 'border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)]'
      }`}
    >
      {plan.badge ? (
        <span
          className={`mb-4 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
            plan.highlighted ? 'bg-white/20' : 'bg-[var(--color-violet-100)] accent-text'
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {plan.badge}
        </span>
      ) : null}
      <h3 className="text-lg font-semibold">{plan.name}</h3>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-[var(--font-display)] text-4xl font-bold">
          {plan.currency}
          {plan.monthlyPrice}
        </span>
        <span className={plan.highlighted ? 'text-white/70' : 'text-[var(--color-ink-muted)]'}>
          /mo
        </span>
      </div>
      {plan.annualPrice ? (
        <p
          className={`mt-1 text-xs ${plan.highlighted ? 'text-white/70' : 'text-[var(--color-ink-muted)]'}`}
        >
          or {plan.currency}
          {plan.annualPrice}/yr
        </p>
      ) : null}
      {plan.headline ? (
        <p
          className={`mt-3 text-sm ${plan.highlighted ? 'text-white/80' : 'text-[var(--color-ink-soft)]'}`}
        >
          {plan.headline}
        </p>
      ) : null}
      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm">
            <Check
              className={`mt-0.5 h-4 w-4 shrink-0 ${plan.highlighted ? 'text-white' : 'accent-text'}`}
              aria-hidden
            />
            <span className={plan.highlighted ? 'text-white/90' : 'text-[var(--color-ink-soft)]'}>
              {feature}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <SmartLink
          href={plan.ctaHref}
          className={`inline-flex w-full items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
            plan.highlighted
              ? 'bg-white text-[var(--accent-ink)] hover:brightness-95'
              : 'accent-bg text-white hover:brightness-110'
          }`}
        >
          {plan.ctaLabel}
        </SmartLink>
      </div>
    </div>
  );
}

function Plans(block: PlansBlock): JSX.Element {
  const [plans, setPlans] = useState<PricingPlan[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchPricing(controller.signal).then(setPlans);
    return () => controller.abort();
  }, []);

  return (
    <Section>
      <SectionHeading
        eyebrow={block.eyebrow}
        heading={block.heading}
        subheading={block.subheading}
      />
      {plans === null ? (
        <p className="mt-12 text-center text-[var(--color-ink-muted)]">Loading plans…</p>
      ) : plans.length === 0 ? (
        <p className="mt-12 text-center text-[var(--color-ink-muted)]">Pricing is being updated.</p>
      ) : (
        <StaggerGroup
          className={`mt-14 grid items-stretch gap-6 ${
            plans.length >= 4 ? 'lg:grid-cols-4 md:grid-cols-2' : 'lg:grid-cols-3'
          }`}
        >
          {plans.map((plan) => (
            <StaggerItem key={plan.code} className="h-full">
              <PlanCard plan={plan} />
            </StaggerItem>
          ))}
        </StaggerGroup>
      )}
    </Section>
  );
}

/* ------------------------------ Testimonials ---------------------------- */

function Testimonials(block: TestimonialsBlock): JSX.Element {
  return (
    <Section>
      <SectionHeading heading={block.heading} />
      <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2">
        {block.items.map((item) => (
          <StaggerItem
            key={item.author}
            className="flex h-full flex-col gap-6 rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-[var(--color-surface)] p-8"
          >
            <Quote className="accent-text h-8 w-8" aria-hidden />
            <blockquote className="font-[var(--font-display)] text-xl leading-snug text-[var(--color-ink)]">
              “{item.quote}”
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3">
              {item.avatarUrl ? (
                <img src={item.avatarUrl} alt="" className="h-11 w-11 rounded-full object-cover" />
              ) : (
                <span className="accent-bg grid h-11 w-11 place-items-center rounded-full font-semibold text-white">
                  {item.author.charAt(0)}
                </span>
              )}
              <span>
                <span className="block font-semibold text-[var(--color-ink)]">{item.author}</span>
                <span className="block text-sm text-[var(--color-ink-muted)]">{item.role}</span>
              </span>
            </figcaption>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </Section>
  );
}

/* ---------------------------------- FAQ --------------------------------- */

function Faq(block: FaqBlock): JSX.Element {
  return (
    <Section tone="white">
      <SectionHeading heading={block.heading} />
      <div className="mx-auto mt-12 max-w-3xl divide-y divide-[var(--color-line)] overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-line)]">
        {block.items.map((item) => (
          <details key={item.question} className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-[var(--color-ink)] transition-colors hover:bg-[var(--color-cream-100)]">
              {item.question}
              <ChevronDown
                className="h-5 w-5 shrink-0 text-[var(--color-ink-muted)] transition-transform group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="px-6 pb-5 leading-relaxed text-[var(--color-ink-soft)]">
              {item.answer}
            </div>
          </details>
        ))}
      </div>
    </Section>
  );
}

/* ---------------------------------- CTA --------------------------------- */

function Cta(block: CtaBlock): JSX.Element {
  return (
    <Section>
      <Reveal className="accent-bg relative overflow-hidden rounded-[2.5rem] px-8 py-16 text-center sm:px-16 sm:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0, transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.25) 0, transparent 40%)',
          }}
          aria-hidden
        />
        <div className="relative mx-auto flex max-w-2xl flex-col items-center gap-5">
          <h2 className="font-[var(--font-display)] text-3xl font-semibold leading-tight text-white sm:text-4xl">
            {block.headline}
          </h2>
          <p className="text-lg leading-relaxed text-white/80">{block.subheadline}</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <SmartLink
              href={block.primaryCta.href}
              className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[var(--accent-ink)] transition-transform duration-200 hover:-translate-y-0.5"
            >
              {block.primaryCta.label}
            </SmartLink>
            {block.secondaryCta.label ? (
              <SmartLink
                href={block.secondaryCta.href}
                className="inline-flex items-center justify-center rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
              >
                {block.secondaryCta.label}
              </SmartLink>
            ) : null}
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/* ------------------------------- Rich text ------------------------------ */

function renderMarkdown(body: string): ReactNode {
  return body.split('\n').map((line, index) => {
    const key = `${index}-${line.slice(0, 12)}`;
    if (line.startsWith('## ')) return <h2 key={key}>{line.slice(3)}</h2>;
    if (line.trim() === '') return null;
    return <p key={key}>{line}</p>;
  });
}

function RichText(block: RichTextBlock): JSX.Element {
  return (
    <section className="py-20 sm:py-28">
      <Container className="max-w-3xl">
        <h1 className="font-[var(--font-display)] text-4xl font-semibold text-[var(--color-ink)]">
          {block.title}
        </h1>
        {block.updatedLabel ? (
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">{block.updatedLabel}</p>
        ) : null}
        <div className="prose-legal mt-10">{renderMarkdown(block.body)}</div>
      </Container>
    </section>
  );
}

/* -------------------------------- Contact ------------------------------- */

function Contact(block: ContactBlock): JSX.Element {
  const details = [
    block.email
      ? { icon: Mail, label: 'Email', value: block.email, href: `mailto:${block.email}` }
      : null,
    block.phone
      ? {
          icon: Phone,
          label: 'Phone',
          value: block.phone,
          href: `tel:${block.phone.replace(/\s/g, '')}`,
        }
      : null,
    block.address ? { icon: MapPin, label: 'Where', value: block.address, href: null } : null,
  ].filter(Boolean) as { icon: typeof Mail; label: string; value: string; href: string | null }[];

  return (
    <section className="py-20 sm:py-28">
      <Container>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div className="flex flex-col gap-6">
            <h1 className="font-[var(--font-display)] text-4xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
              {block.heading}
            </h1>
            <p className="text-lg leading-relaxed text-[var(--color-ink-soft)]">
              {block.subheading}
            </p>
            <div className="mt-4 space-y-3">
              {details.map((detail) => {
                const Icon = detail.icon;
                const content = (
                  <span className="flex items-center gap-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] px-5 py-4">
                    <span className="accent-text grid h-11 w-11 place-items-center rounded-xl bg-[var(--color-violet-100)]">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                        {detail.label}
                      </span>
                      <span className="block font-medium text-[var(--color-ink)]">
                        {detail.value}
                      </span>
                    </span>
                  </span>
                );
                return detail.href ? (
                  <a
                    key={detail.label}
                    href={detail.href}
                    className="block transition-transform hover:-translate-y-0.5"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={detail.label}>{content}</div>
                );
              })}
            </div>
          </div>
          <ContactForm />
        </div>
      </Container>
    </section>
  );
}

function ContactForm({ formKey = 'contact' }: { formKey?: string }): JSX.Element {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setState('sending');
    const result = await submitForm(formKey, { ...form, consent });
    setMessage(result.message);
    setState(result.ok ? 'done' : 'error');
  }

  if (state === 'done') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-[var(--color-surface)] p-10 text-center">
        <span className="accent-bg grid h-14 w-14 place-items-center rounded-full text-white">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <h3 className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-ink)]">
          Message sent
        </h3>
        <p className="text-[var(--color-ink-soft)]">{message}</p>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-5 rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-[var(--color-surface)] p-8"
      onSubmit={(e) => void onSubmit(e)}
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="Name"
          name="name"
          value={form.name}
          onChange={set('name')}
          placeholder="Priya Nair"
        />
        <Field
          label="Work email"
          name="email"
          type="email"
          required
          value={form.email}
          onChange={set('email')}
          placeholder="priya@company.com"
        />
      </div>
      <Field
        label="Company"
        name="company"
        value={form.company}
        onChange={set('company')}
        placeholder="Lumen Spa"
      />
      <label className="flex flex-col gap-2 text-sm font-medium text-[var(--color-ink)]">
        How can we help?
        <textarea
          name="message"
          rows={4}
          value={form.message}
          onChange={(e) => set('message')(e.target.value)}
          placeholder="Tell us about your inbox…"
          className="rounded-xl border border-[var(--color-line)] bg-[var(--color-cream-100)] px-4 py-3 text-[var(--color-ink)] outline-none transition focus:border-[var(--accent)] focus:bg-[var(--color-surface)]"
        />
      </label>
      <label className="flex items-start gap-2.5 text-sm text-[var(--color-ink-soft)]">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
        />
        I agree to be contacted about Vaanexa and accept the privacy policy.
      </label>
      {state === 'error' && (
        <p role="alert" className="text-sm font-medium text-red-600">
          {message}
        </p>
      )}
      <button
        type="submit"
        disabled={state === 'sending' || !form.email || !consent}
        className="accent-bg inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
      >
        {state === 'sending' ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

function Field({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-2 text-sm font-medium text-[var(--color-ink)]">
      <span>
        {label}
        {required ? <span className="accent-text"> *</span> : null}
      </span>
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border border-[var(--color-line)] bg-[var(--color-cream-100)] px-4 py-3 text-[var(--color-ink)] outline-none transition focus:border-[var(--accent)] focus:bg-[var(--color-surface)]"
      />
    </label>
  );
}

/* ------------------------------ Dispatcher ------------------------------ */

export function BlockRenderer({ block }: { block: PageBlock }): JSX.Element | null {
  switch (block.type) {
    case 'hero':
      return <HeroCarousel content={deriveHeroContent(block)} />;
    case 'logos':
      return <Logos {...block} />;
    case 'stats':
      return <Stats {...block} />;
    case 'feature_grid':
      return <FeatureGrid {...block} />;
    case 'feature_split':
      return <FeatureSplit {...block} />;
    case 'pricing':
      return <Pricing {...block} />;
    case 'plans':
      return <Plans {...block} />;
    case 'testimonials':
      return <Testimonials {...block} />;
    case 'faq':
      return <Faq {...block} />;
    case 'cta':
      return <Cta {...block} />;
    case 'rich_text':
      return <RichText {...block} />;
    case 'contact':
      return <Contact {...block} />;
    default:
      return null;
  }
}
